/**
 * Declarative sizing template type definitions.
 *
 * Templates define the keypoints and measurements for a specific workflow
 * (e.g., EVAR, TEVAR). They are TypeScript constants — type-safe, fixed in code.
 */

// ============================================================================
// Enums
// ============================================================================

export type MeasurementSheetType = 'length' | 'diameter' | 'angle' | 'conicity';

export enum MeasurementType {
    LENGTH = 'length',
    DIAMETER = 'diameter',
    OFFSET_DIAMETER = 'offset-diameter',
    MIN_MAX_DIAMETER = 'min-max-diameter',
    ANGLE = 'angle',
    DERIVED = 'derived',
}

export enum MeasurementUnit {
    MM = 'mm',
    DEGREES = '°',
    PERCENT = '%',
}

export enum MeasurementCategory {
    LENGTH = 'lengths',
    DIAMETER = 'diameters',
    ANGLE = 'angles',
    DERIVED = 'derived',
}

// ============================================================================
// Template Root
// ============================================================================

export interface ISizingTemplate {
    readonly id: string;
    readonly name: string;
    readonly keypoints: readonly IKeypointDefinition[];
    readonly measurements: readonly IMeasurementDefinition[];
}

// ============================================================================
// Keypoint Definition
// ============================================================================

export interface IKeypointDefinition {
    /** Unique identifier, e.g. "P2" */
    readonly id: string;
    /** Clinical name, e.g. "Lowest Renal Artery" */
    readonly name: string;
    /** Short label for 3D overlay, e.g. "P2" */
    readonly shortLabel: string;
    /** Whether the user can reposition this keypoint */
    readonly editable: boolean;
    /** When true, ghost placements are created on other centerline legs at the same root distance. */
    readonly ghostOnOtherLegs?: boolean;
}

// ============================================================================
// Measurement Definitions (discriminated union)
// ============================================================================

export type IMeasurementDefinition =
    | ILengthMeasurementDef
    | IDiameterMeasurementDef
    | IOffsetDiameterMeasurementDef
    | IMinMaxDiameterMeasurementDef
    | IAngleMeasurementDef
    | IDerivedMeasurementDef;

/** Centerline-following distance between two keypoints. */
export interface ILengthMeasurementDef {
    readonly type: MeasurementType.LENGTH;
    readonly id: string;
    readonly name: string;
    /** Short label for 3D/CPR overlays where space is limited */
    readonly shortLabel: string;
    readonly category: MeasurementCategory.LENGTH;
    readonly fromKeypointId: string;
    readonly toKeypointId: string;
}

/** Vessel diameter at a keypoint's nearest centerline node. */
export interface IDiameterMeasurementDef {
    readonly type: MeasurementType.DIAMETER;
    readonly id: string;
    readonly name: string;
    /** Short label for 3D/CPR overlays where space is limited */
    readonly shortLabel: string;
    readonly category: MeasurementCategory.DIAMETER;
    readonly keypointId: string;
}

/** Diameter at a fixed distance from a keypoint along the centerline. */
export interface IOffsetDiameterMeasurementDef {
    readonly type: MeasurementType.OFFSET_DIAMETER;
    readonly id: string;
    readonly name: string;
    /** Short label for 3D/CPR overlays where space is limited */
    readonly shortLabel: string;
    readonly category: MeasurementCategory.DIAMETER;
    /** Reference keypoint to offset from */
    readonly keypointId: string;
    /** Distance in mm. Positive = distal (toward bifurcation), negative = proximal */
    readonly offsetMm: number;
    /** Whether this is a default offset from the template (vs. user-added) */
    readonly isDefault: boolean;
}

/** Min or max diameter over a range of centerline nodes between two keypoints. */
export interface IMinMaxDiameterMeasurementDef {
    readonly type: MeasurementType.MIN_MAX_DIAMETER;
    readonly id: string;
    readonly name: string;
    /** Short label for 3D/CPR overlays where space is limited */
    readonly shortLabel: string;
    readonly category: MeasurementCategory.DIAMETER;
    readonly fromKeypointId: string;
    readonly toKeypointId: string;
    readonly mode: 'min' | 'max';
}

/** Angle formed by a triplet of keypoints (vertex at the middle one). */
export interface IAngleMeasurementDef {
    readonly type: MeasurementType.ANGLE;
    readonly id: string;
    readonly name: string;
    /** Short label for 3D/CPR overlays where space is limited */
    readonly shortLabel: string;
    readonly category: MeasurementCategory.ANGLE;
    /** Three keypoint IDs forming the angle; vertex at index 1 */
    readonly keypointIds: readonly [string, string, string];
    /** 'supplementary' computes 180 - angle */
    readonly formula: 'direct' | 'supplementary';
}

/** Computed from other measurements via a pure function. */
export interface IDerivedMeasurementDef {
    readonly type: MeasurementType.DERIVED;
    readonly id: string;
    readonly name: string;
    /** Short label for 3D/CPR overlays where space is limited */
    readonly shortLabel: string;
    readonly category: MeasurementCategory.DERIVED;
    /** IDs of measurements this depends on */
    readonly dependencies: readonly string[];
    /** Pure function computing the derived value from resolved dependency values */
    readonly compute: (dependencyValues: Record<string, number>) => number;
}
